import { Link } from "react-router-dom";
import { Sparkles, CheckSquare, MessageCircle, AlarmClock, Bookmark, Radio, Newspaper, Wand2, Move, ArrowRight, StickyNote, Rss } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { WhatsAppIcon } from "@/components/cards/WhatsAppCard";

const FEATURES = [
  { icon: CheckSquare, title: "Tasks & Roles", desc: "Create, assign and track tasks with Admin, Manager and Executive powers built in.", color: "from-emerald-400 to-teal-500" },
  { icon: MessageCircle, title: "Team Chat", desc: "Channels, DMs, images and files — realtime, right on your board.", color: "from-sky-400 to-blue-500" },
  { icon: WhatsAppIcon, title: "WhatsApp Inside", desc: "Link your WhatsApp with a QR and chat with anyone without leaving Nexfy.", color: "from-green-400 to-emerald-600" },
  { icon: AlarmClock, title: "Notes & Reminders", desc: "Sticky notes and reminders that actually remind you — with countdowns.", color: "from-orange-400 to-amber-500" },
  { icon: Bookmark, title: "Bookmarks", desc: "Your favourite links as a tidy favicon grid, one click away.", color: "from-violet-400 to-purple-500" },
  { icon: Radio, title: "Pings", desc: "Nudge a teammate instantly. They'll feel it — live, in under a second.", color: "from-pink-400 to-rose-500" },
  { icon: Newspaper, title: "News & Feeds", desc: "Indian headlines plus any RSS feed — AI, English, marketing, you name it.", color: "from-slate-400 to-slate-600" },
  { icon: Wand2, title: "12 Vibes", desc: "Restyle your whole artboard by mood — Happy, Excited, Sad or Heartbroken.", color: "from-fuchsia-400 to-violet-600" },
  { icon: Move, title: "Drag Everything", desc: "Freeform canvas. Drag, resize, recolor and connect cards with arrows.", color: "from-cyan-400 to-sky-600" },
];

const VIBE_STRIP = [
  "linear-gradient(135deg,#FFFBEB,#FDE68A)", "linear-gradient(135deg,#FDF2F8,#FBCFE8)",
  "linear-gradient(135deg,#0F172A,#312E81)", "linear-gradient(135deg,#2E1065,#701A75)",
  "linear-gradient(135deg,#F1F5F9,#CBD5E1)", "linear-gradient(135deg,#0B1120,#1E3A8A)",
  "linear-gradient(135deg,#FFF1F2,#FECDD3)", "linear-gradient(135deg,#09090B,#27272A)",
];

export default function LandingPage() {
  const { user } = useAuth();
  const cta = user && user !== false ? { to: "/app", label: "Open my board →" } : { to: "/signup", label: "Get Started — it's free" };

  return (
    <div className="min-h-screen bg-[#0B0F19] text-white" data-testid="landing-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b border-white/10 bg-[#0B0F19]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-5 py-3.5">
          <div className="flex items-center gap-1.5 font-display text-xl font-extrabold">
            <Sparkles className="h-5 w-5 text-violet-400" />
            <span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">Nexfy</span>
          </div>
          <div className="hidden gap-6 text-sm text-slate-300 sm:flex ml-6">
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#vibes" className="hover:text-white transition-colors">Vibes</a>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/login" data-testid="landing-login-link"
              className="rounded-xl px-4 py-2 text-sm text-slate-300 transition-colors hover:text-white">Log in</Link>
            <Link to={cta.to} data-testid="landing-nav-cta"
              className="rounded-xl bg-gradient-to-r from-violet-500 to-pink-500 px-4 py-2 text-sm font-bold shadow-lg shadow-violet-500/25 transition-transform hover:scale-105">
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="relative overflow-hidden">
        <div className="blob absolute -top-24 -left-24 h-96 w-96 rounded-full bg-violet-600/25 blur-3xl" />
        <div className="blob absolute top-40 -right-24 h-96 w-96 rounded-full bg-pink-600/20 blur-3xl" style={{ animationDelay: "-4s" }} />
        <div className="absolute inset-0 canvas-dots opacity-30" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-5 py-16 sm:py-24 lg:grid-cols-2">
          <div>
            <p className="mb-4 inline-block rounded-full border border-violet-400/30 bg-violet-500/10 px-3 py-1 font-mono text-xs uppercase tracking-wider text-violet-300">
              ✦ your full-day screen dashboard
            </p>
            <h1 className="font-display text-4xl font-extrabold leading-tight sm:text-5xl lg:text-6xl">
              Your workboard,<br />
              <span className="bg-gradient-to-r from-violet-400 via-pink-400 to-amber-300 bg-clip-text text-transparent">your vibes.</span>
            </h1>
            <p className="mt-5 max-w-md text-base text-slate-300 sm:text-lg">
              Tasks, chats, WhatsApp, notes, news and vibes — everything your day needs, on one freeform drag-and-drop canvas.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={cta.to} data-testid="landing-hero-cta"
                className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-500 to-pink-500 px-6 py-3.5 font-bold shadow-xl shadow-violet-500/30 transition-transform hover:scale-105 active:scale-95">
                {cta.label} <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#features" data-testid="landing-hero-secondary"
                className="rounded-xl border border-white/15 bg-white/5 px-6 py-3.5 font-semibold text-slate-200 transition-colors hover:bg-white/10">
                See what's inside
              </a>
            </div>
            <p className="mt-4 font-hand text-xl text-pink-300 rotate-[-1deg]">no credit card. just vibes ✦</p>
          </div>

          {/* CSS mock of the artboard */}
          <div className="relative hidden h-[420px] select-none lg:block" data-testid="landing-hero-mock">
            <div className="card-pop absolute left-0 top-6 w-60 -rotate-3 rounded-2xl border-2 border-emerald-400 bg-emerald-50 p-3 text-emerald-900 shadow-2xl">
              <div className="flex items-center gap-1.5 text-xs font-bold"><CheckSquare className="h-3.5 w-3.5" /> Task</div>
              <p className="mt-1.5 font-display text-sm font-bold">Launch Nexfy 🚀</p>
              <div className="mt-2 flex gap-1.5">
                <span className="rounded-full bg-red-500/20 px-2 py-0.5 text-[10px] font-bold text-red-700">high</span>
                <span className="rounded-full bg-black/10 px-2 py-0.5 text-[10px]">→ Maya</span>
              </div>
            </div>
            <div className="card-pop absolute right-4 top-0 w-56 rotate-2 rounded-2xl border-2 border-yellow-400 bg-yellow-50 p-3 text-yellow-900 shadow-2xl" style={{ animationDelay: "0.15s" }}>
              <div className="flex items-center gap-1.5 text-xs font-bold"><StickyNote className="h-3.5 w-3.5" /> Note</div>
              <p className="mt-1.5 text-sm">Small steps today.<br />Big things tomorrow. ✦</p>
            </div>
            <div className="card-pop absolute left-16 top-44 w-64 rotate-1 rounded-2xl border-2 border-sky-400 bg-sky-50 p-3 text-sky-900 shadow-2xl" style={{ animationDelay: "0.3s" }}>
              <div className="flex items-center gap-1.5 text-xs font-bold"><MessageCircle className="h-3.5 w-3.5" /> Chat</div>
              <div className="mt-2 rounded-xl bg-white px-2.5 py-1.5 text-xs shadow-sm">The new board looks amazing! 🔥</div>
              <div className="mt-1.5 ml-8 rounded-xl bg-violet-500 px-2.5 py-1.5 text-xs text-white shadow-sm">ikr — pure vibes ✨</div>
            </div>
            <div className="card-pop absolute right-0 top-56 w-52 -rotate-2 rounded-2xl border-2 border-pink-400 bg-pink-50 p-3 text-pink-900 shadow-2xl" style={{ animationDelay: "0.45s" }}>
              <div className="flex items-center gap-1.5 text-xs font-bold"><Radio className="h-3.5 w-3.5" /> Ping</div>
              <p className="mt-1.5 text-xs">📣 Akshay pinged you</p>
              <button className="mt-2 rounded-full bg-gradient-to-r from-pink-500 to-violet-500 px-3 py-1 text-[10px] font-bold text-white">Ping back</button>
            </div>
            <p className="absolute -bottom-2 left-24 font-hand text-2xl text-violet-300 rotate-[-4deg]">drag anything, anywhere →</p>
          </div>
        </div>
      </header>

      {/* Features */}
      <section id="features" className="relative border-t border-white/5 py-20">
        <div className="mx-auto max-w-6xl px-5">
          <h2 className="text-center font-display text-base font-bold text-violet-300 md:text-lg uppercase tracking-widest">Everything on one canvas</h2>
          <p className="mx-auto mt-3 max-w-xl text-center font-display text-3xl font-extrabold sm:text-4xl">
            One board. <span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">Your whole day.</span>
          </p>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <div key={f.title} data-testid={`landing-feature-${i}`}
                className="group rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur transition-all hover:-translate-y-1 hover:border-violet-400/40 hover:bg-white/10">
                <div className={`mb-4 inline-flex rounded-xl bg-gradient-to-br ${f.color} p-2.5 shadow-lg`}>
                  <f.icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-display text-lg font-bold">{f.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vibes */}
      <section id="vibes" className="border-t border-white/5 py-20">
        <div className="mx-auto max-w-6xl px-5 text-center">
          <p className="font-display text-3xl font-extrabold sm:text-4xl">
            Boards that match <span className="bg-gradient-to-r from-amber-300 to-pink-400 bg-clip-text text-transparent">your mood.</span>
          </p>
          <p className="mx-auto mt-3 max-w-md text-slate-400">Happy, Excited, Sad or Heartbroken — flip the entire artboard's vibe in one tap.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            {VIBE_STRIP.map((bg, i) => (
              <div key={i} className="h-16 w-24 rounded-2xl border border-white/15 shadow-lg transition-transform hover:scale-110 hover:-rotate-3" style={{ background: bg }} />
            ))}
          </div>
          <p className="mt-6 font-hand text-2xl text-violet-300 rotate-[-2deg]">12 vibes. infinite feels ✦</p>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/5 py-20">
        <div className="relative mx-auto max-w-4xl overflow-hidden rounded-3xl bg-gradient-to-r from-violet-600 to-pink-600 px-6 py-14 text-center shadow-2xl shadow-violet-500/20 sm:px-12">
          <div className="absolute inset-0 canvas-dots opacity-20" />
          <h2 className="relative font-display text-3xl font-extrabold sm:text-4xl">Get started with Nexfy today</h2>
          <p className="relative mx-auto mt-3 max-w-md text-white/85">Your tasks, your chats, your news, your vibes — all saved, all yours, on every device.</p>
          <Link to={cta.to} data-testid="landing-bottom-cta"
            className="relative mt-8 inline-flex items-center gap-2 rounded-xl bg-white px-8 py-3.5 font-bold text-violet-700 shadow-xl transition-transform hover:scale-105 active:scale-95">
            {cta.label} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-3 px-5 sm:flex-row">
          <div className="flex items-center gap-1.5 font-display font-extrabold">
            <Sparkles className="h-4 w-4 text-violet-400" />
            <span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">Nexfy</span>
          </div>
          <span className="text-xs text-slate-500">nexfy.in — your workboard, your vibes ✦</span>
          <div className="flex gap-5 text-xs text-slate-400 sm:ml-auto">
            <a href="#features" className="hover:text-white">Features</a>
            <a href="#vibes" className="hover:text-white">Vibes</a>
            <Link to="/login" className="hover:text-white">Log in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
